/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ExecutionSummaryV2 } from '@flux/types';
import type { WorkerOutbound } from './worker-types';

interface ExecutionContextValue {
  executions: ExecutionSummaryV2[];
  isLoading: boolean;
  error: string | null;
  isSyncing: boolean;
  lastSyncAt: number | null;
  coveragePct: number;
  // Debug surface — exposed for the ?debug=1 panel
  workerId: string | null;
  inFlight: number;
  workerState: string | null;
  gapCount: number;
  lastErrorAt: number | null;
  clearAll: () => void;
  forcePoll: () => void;
  getExecution: (id: string) => ExecutionSummaryV2 | undefined;
  getDetails: (executionId: string, projectId?: string | null) => Promise<unknown[]>;
}

const ExecutionContext = createContext<ExecutionContextValue | null>(null);

/** How often to send a HEALTH_PING to the worker (ms). */
const HEALTH_PING_INTERVAL_MS = 10_000;
/** Restart the worker if no HEALTH_PONG is received within this window (ms). */
const HEALTH_PONG_TIMEOUT_MS = 30_000;

export function ExecutionProvider({
  children,
}: {
  children: React.ReactNode;
}): import('react/jsx-runtime').JSX.Element {
  const [executions, setExecutions] = useState<ExecutionSummaryV2[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
  const [coveragePct, setCoveragePct] = useState(0);
  const [workerId, setWorkerId] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState(0);
  const [workerState, setWorkerState] = useState<string | null>(null);
  const [gapCount, setGapCount] = useState(0);
  const [lastErrorAt, setLastErrorAt] = useState<number | null>(null);

  const queryClient = useQueryClient();

  const workerRef = useRef<SharedWorker | null>(null);
  /** Tracks distinct workerIds seen — warn if multiple different workers respond. */
  const seenWorkerIds = useRef(new Set<string>());
  /** Timestamp of last HEALTH_PONG received. */
  const lastPongAt = useRef<number>(Date.now());
  const healthIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleMessage = useCallback((e: MessageEvent): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const msg = e.data as WorkerOutbound;
    const { type } = msg;

    // E2E/ops signal: any message means the worker is alive (not SPA-fallback HTML).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__executionWorkerSeenAt = Date.now();

    switch (type) {
      case 'SYNC_COMPLETE':
        setExecutions(msg.data);
        setIsLoading(false);
        setError(null);
        setIsSyncing(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__executionWorkerReady = true;
        break;

      case 'STATUS_OK':
        if (msg.data?.length) setExecutions(msg.data);
        setIsSyncing(msg.isPolling);
        if (msg.hasKey) {
          setIsLoading(false);
        } else {
          workerRef.current?.port.postMessage({ v: 1, type: 'INIT' });
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__executionWorkerReady = true;
        break;

      case 'WORKER_READY': {
        const { workerId: wid } = msg;
        if (seenWorkerIds.current.size > 0 && !seenWorkerIds.current.has(wid)) {
          console.warn('[ExecutionStore] Multiple distinct workerIds detected', [
            ...seenWorkerIds.current,
            wid,
          ]);
        }
        seenWorkerIds.current.add(wid);
        setWorkerId(wid);
        setIsLoading(false);
        setError(null);
        // E2E/ops signal: proves the SharedWorker loaded and responded.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__executionWorkerReady = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__executionWorkerReadyAt = Date.now();
        workerRef.current?.port.postMessage({ v: 1, type: 'FORCE_POLL' });
        break;
      }

      case 'SYNC_ERROR':
        console.error('[ExecutionStore] Worker error:', msg.error);
        setError(msg.error);
        setIsLoading(false);
        setIsSyncing(false);
        break;

      case 'CLEARED':
        setExecutions([]);
        setIsSyncing(false);
        // Worker cleared state due to session loss (401). refetchQueries with
        // type:'active' implicitly invalidates and immediately refetches only
        // the queries that are currently mounted — single scheduling pass,
        // no duplicate work, identity flips on the next render.
        void queryClient.refetchQueries({ queryKey: ['profile'], type: 'active' });
        break;

      case 'HEALTH_PONG':
        lastPongAt.current = Date.now();
        setCoveragePct(msg.coveragePct);
        if (msg.lastSyncAt !== null) setLastSyncAt(msg.lastSyncAt);
        setIsSyncing(msg.inFlight > 0);
        setInFlight(msg.inFlight);
        setWorkerState(msg.state);
        setGapCount(msg.gapCount);
        if (msg.lastErrorAt !== null) setLastErrorAt(msg.lastErrorAt);
        break;

      case 'VERSION_MISMATCH':
        console.error(
          '[ExecutionStore] Worker version mismatch — expected v1, got',
          msg.received,
          '— reloading',
        );
        window.location.reload();
        break;

      default:
        // Unknown message type from worker — ignore
        break;
    }
  }, []);

  const startWorker = useCallback((): void => {
    if (typeof window === 'undefined') return;
    if (workerRef.current) return;

    try {
      // Vite bundles the worker and returns a content-hashed URL automatically.
      const workerUrl = new URL('../../workers/execution.worker.ts', import.meta.url);
      if (!workerUrl.pathname.endsWith('execution.worker.ts')) {
        console.error('[ExecutionStore] Invalid worker URL', workerUrl.toString());
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__executionWorkerUrl = workerUrl.toString();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__executionWorkerStartedAt = Date.now();
      const worker = new SharedWorker(workerUrl, {
        type: 'module',
        name: 'flux-execution-worker',
      });
      workerRef.current = worker;

      worker.port.onmessage = handleMessage;
      worker.port.onmessageerror = (err): void => {
        console.error('[ExecutionStore] Port message error:', err);
      };
      worker.onerror = (err): void => {
        console.error('[ExecutionStore] Worker error:', err);
        // A hard worker load failure (e.g. stale tab on a new deploy) is
        // unrecoverable without a fresh page. Reload so the browser fetches
        // the new worker bundle.
        //
        // Guard against an infinite reload storm: only reload once per 5s.
        // A persistent failure (bad deploy) will stop reloading after the
        // first attempt and surface the error via setError instead.
        const RELOAD_KEY = 'worker_crash_reload';
        const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');
        if (Date.now() - lastReload > 5_000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
        } else {
          setError('Execution worker failed to load. Please refresh manually.');
          setIsLoading(false);
        }
      };

      worker.port.start();
      worker.port.postMessage({ v: 1, type: 'CHECK_STATUS' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[ExecutionStore] Worker setup failed:', err);
      setError(msg);
      setIsLoading(false);
    }
  }, [handleMessage]);

  const restartWorker = useCallback((): void => {
    if (workerRef.current) {
      workerRef.current.port.postMessage({ v: 1, type: 'DISCONNECT' });
      workerRef.current = null;
    }
    seenWorkerIds.current.clear();
    startWorker();
  }, [startWorker]);

  useEffect(() => {
    startWorker();

    // Health watchdog: ping every 10s, restart if silent for 30s.
    healthIntervalRef.current = setInterval(() => {
      workerRef.current?.port.postMessage({ v: 1, type: 'HEALTH_PING' });
      if (Date.now() - lastPongAt.current > HEALTH_PONG_TIMEOUT_MS) {
        console.warn('[ExecutionStore] Worker unresponsive — restarting');
        restartWorker();
      }
    }, HEALTH_PING_INTERVAL_MS);

    return (): void => {
      if (healthIntervalRef.current) {
        clearInterval(healthIntervalRef.current);
        healthIntervalRef.current = null;
      }
      // Intentionally keep the SharedWorker alive for tab-switcher perf.
      // Other tabs still hold their ports.
    };
  }, [startWorker, restartWorker]);

  const clearAll = useCallback((): void => {
    workerRef.current?.port.postMessage({ v: 1, type: 'CLEAR' });
  }, []);

  const forcePoll = useCallback((): void => {
    workerRef.current?.port.postMessage({ v: 1, type: 'FORCE_POLL' });
  }, []);

  const getExecution = useCallback(
    (id: string): ExecutionSummaryV2 | undefined => {
      return executions.find((e): boolean => e.executionId === id);
    },
    [executions],
  );

  const getDetails = useCallback(
    async (executionId: string, projectId?: string | null): Promise<unknown[]> => {
      if (!workerRef.current) throw new Error('Worker not ready');

      return new Promise<unknown[]>((resolve, reject) => {
        const channel = new MessageChannel();
        const timeout = setTimeout(() => {
          try {
            channel.port1.close();
          } catch {
            // ignore
          }
          reject(new Error('Timeout waiting for worker details'));
        }, 30_000);

        channel.port1.onmessage = (event): void => {
          const msg = event.data as WorkerOutbound;
          clearTimeout(timeout);
          try {
            channel.port1.close();
          } catch {
            // ignore
          }
          if (msg.type === 'DETAILS_OK') resolve(msg.payload);
          else if (msg.type === 'DETAILS_ERROR') reject(new Error(msg.error));
          else reject(new Error('Unexpected response from worker'));
        };

        workerRef.current?.port.postMessage(
          {
            v: 1,
            type: 'GET_DETAILS',
            payload: { executionId, projectId: projectId ?? undefined },
          },
          [channel.port2],
        );
      });
    },
    [],
  );

  return (
    <ExecutionContext.Provider
      value={{
        executions,
        isLoading,
        error,
        isSyncing,
        lastSyncAt,
        coveragePct,
        workerId,
        inFlight,
        workerState,
        gapCount,
        lastErrorAt,
        clearAll,
        forcePoll,
        getExecution,
        getDetails,
      }}
    >
      {children}
    </ExecutionContext.Provider>
  );
}

export function useExecutionStore(): ExecutionContextValue {
  const context = useContext(ExecutionContext);
  if (!context) {
    throw new Error('useExecutionStore must be used within an ExecutionProvider');
  }
  return context;
}

export function useOptionalExecutionStore(): ExecutionContextValue | null {
  return useContext(ExecutionContext);
}
